import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import type { DateFilterType } from "./FiltersPanel";

export type TimePeriod = 'morning' | 'afternoon' | 'evening' | 'allDay';

interface DateFilterPillsProps {
  dateFilter: DateFilterType;
  onDateFilterChange: (filter: DateFilterType, customDate: string | null) => void;
  timePeriods: TimePeriod[];
  onTimePeriodChange: (periods: TimePeriod[]) => void;
}

export default function DateFilterPills({ 
  dateFilter, 
  onDateFilterChange,
  timePeriods = ['allDay'],
  onTimePeriodChange
}: DateFilterPillsProps) {
  const [showTimeFilters, setShowTimeFilters] = useState(dateFilter !== null);
  const [highlightAllDay, setHighlightAllDay] = useState(false);
  const allDayButtonRef = useRef<HTMLButtonElement>(null);
  
  const safeTimePeriods = timePeriods ?? ['allDay'];
  
  const datePills: { label: string; value: DateFilterType }[] = [
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "This Weekend", value: "weekend" },
  ];

  const timePills: { label: string; value: TimePeriod }[] = [
    { label: "Morning", value: "morning" },
    { label: "Afternoon", value: "afternoon" },
    { label: "Evening", value: "evening" },
    { label: "All Day", value: "allDay" },
  ];

  useEffect(() => {
    if (dateFilter !== null) {
      setShowTimeFilters(true);
    } else {
      const timer = setTimeout(() => setShowTimeFilters(false), 200);
      return () => clearTimeout(timer);
    }
  }, [dateFilter]);

  const handleDatePillClick = (value: DateFilterType) => {
    if (dateFilter === value) {
      onDateFilterChange(null, null);
    } else {
      onDateFilterChange(value, null);
    }
  };

  const handleTimePillClick = (clicked: TimePeriod) => {
    let newPeriods: TimePeriod[];
    
    if (clicked === 'allDay') {
      newPeriods = ['allDay'];
    } else {
      const currentWithoutAllDay = safeTimePeriods.filter(p => p !== 'allDay');
      
      if (currentWithoutAllDay.includes(clicked)) {
        newPeriods = currentWithoutAllDay.filter(p => p !== clicked);
      } else {
        newPeriods = [...currentWithoutAllDay, clicked];
      }
      
      const hasAllThree = 
        newPeriods.includes('morning') && 
        newPeriods.includes('afternoon') && 
        newPeriods.includes('evening');
      
      if (hasAllThree) {
        newPeriods = ['allDay'];
        setHighlightAllDay(true);
        setTimeout(() => setHighlightAllDay(false), 400);
      }
      
      if (newPeriods.length === 0) {
        newPeriods = ['allDay'];
      }
    }
    
    onTimePeriodChange(newPeriods);
  };

  const isTimePeriodSelected = (period: TimePeriod) => {
    return safeTimePeriods.includes(period);
  };

  const pillActiveClass = "bg-green-accent text-green-accent-foreground border-green-accent shadow-sm";
  const pillInactiveClass = "bg-card text-muted-foreground border-border/50 hover:border-muted-foreground/50 hover:bg-muted/30";

  return (
    <div className="space-y-2" data-testid="date-filter-pills">
      <div 
        className="flex items-center gap-2 flex-wrap" 
        role="group" 
        aria-label="Filter by date range"
      >
        {datePills.map((pill) => {
          const isActive = dateFilter === pill.value;
          return (
            <Button
              key={pill.value}
              variant="outline"
              size="sm"
              aria-pressed={isActive}
              onClick={() => handleDatePillClick(pill.value)}
              className={`rounded-full px-4 h-8 text-sm font-medium transition-all border ${
                isActive ? pillActiveClass : pillInactiveClass
              }`}
              data-testid={`button-date-${pill.value}`}
            >
              {pill.label}
            </Button>
          );
        })}
      </div>
      
      <div 
        className={`overflow-hidden transition-all duration-200 ease-out ${
          showTimeFilters ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div 
          className={`flex items-center gap-2 flex-wrap pt-2 transition-transform duration-200 ease-out ${
            showTimeFilters ? "translate-y-0" : "-translate-y-2"
          }`}
          role="group"
          aria-label="Filter by time of day"
        >
          {timePills.map((pill) => {
            const isSelected = isTimePeriodSelected(pill.value);
            const isHighlighted = pill.value === 'allDay' && highlightAllDay;
            
            return (
              <button
                key={pill.value}
                ref={pill.value === 'allDay' ? allDayButtonRef : undefined}
                role="checkbox"
                aria-checked={isSelected}
                onClick={() => handleTimePillClick(pill.value)}
                className={`rounded-full px-4 h-8 text-sm font-medium transition-all border flex items-center gap-1.5 ${
                  isSelected 
                    ? pillActiveClass 
                    : pillInactiveClass
                } ${
                  isHighlighted 
                    ? 'ring-2 ring-green-accent ring-offset-2 ring-offset-background animate-pulse' 
                    : ''
                }`}
                data-testid={`button-time-${pill.value}`}
              >
                {pill.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function parseEventStartHour(timeStr: string | null | undefined): number | null {
  if (!timeStr || timeStr.trim() === '') return null;
  
  const normalized = timeStr.trim().toLowerCase();
  const match = normalized.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)?/i);
  if (!match) return null;
  
  let hours = parseInt(match[1], 10);
  const period = match[3];
  
  if (period) {
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;
  }
  
  return hours;
}

export function getTimePeriodForHour(hour: number): TimePeriod {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

export function filterEventsByTimePeriods<T extends { time?: string | null }>(
  events: T[],
  timePeriods: TimePeriod[]
): T[] {
  if (timePeriods.includes('allDay')) {
    return events;
  }
  
  return events.filter(event => {
    const hour = parseEventStartHour(event.time);
    
    // Events without a parseable time are only shown when "All Day" is selected
    // This ensures Morning/Afternoon/Evening filters immediately hide timeless events
    if (hour === null) {
      return false;
    }
    
    const eventPeriod = getTimePeriodForHour(hour);
    return timePeriods.includes(eventPeriod);
  });
}
